import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useGuestDemo } from '@/contexts/GuestDemoContext';
import Pressable from '@/components/base/Pressable';
import DemoGate from '@/components/feature/DemoGate';

export default function ScanCamera() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { canScan, useScan, activeGate, dismissGate } = useGuestDemo();
  const isDark = resolvedTheme === 'dark';

  const consumeScan = useScan;

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textSecondary: isDark ? 'text-neutral-500' : 'text-neutral-400',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    btnSecondary: isDark ? 'bg-neutral-800 text-white' : 'bg-neutral-100 text-black',
    btnOverlay: isDark ? 'bg-black/40' : 'bg-white/60',
    overlayTint: isDark ? 'bg-black/30' : 'bg-black/20',
    frameBorder: isDark ? 'border-2 border-white/50' : 'border-2 border-black/30',
    frameCorner: isDark ? 'border-white' : 'border-black/50',
    hintText: isDark ? 'text-white/80' : 'text-black/70',
    hintBg: isDark ? 'bg-black/40' : 'bg-white/60',
    controlBg: isDark ? 'bg-white/15' : 'bg-black/15',
    captureRing: isDark ? 'border-white' : 'border-black',
    captureFill: isDark ? 'bg-white' : 'bg-black',
    bottomBar: isDark ? 'bg-black/80' : 'bg-white/90',
  };

  const videoRef = useRef<HTMLVideoElement>(null);

  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [showFlash, setShowFlash] = useState(false);

  // Redirect if demo scan already used
  useEffect(() => {
    if (!canScan) {
      navigate('/scan/start');
    }
  }, [canScan, navigate]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
      setCameraError(false);
    } catch {
      setCameraError(true);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  const takePicture = useCallback(() => {
    if (!videoRef.current) return;
    if (!consumeScan()) {
      navigate('/scan/start');
      return;
    }
    setIsCapturing(true);
    setShowFlash(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      setCapturedImage(dataUrl);
      sessionStorage.setItem('scan_input_image', dataUrl);
    }

    setTimeout(() => setShowFlash(false), 180);
    setTimeout(() => setIsCapturing(false), 300);
  }, [consumeScan, navigate]);

  const retake = useCallback(() => {
    setCapturedImage(null);
    setCameraActive(false);
    setTimeout(() => startCamera(), 250);
  }, [startCamera]);

  const confirm = useCallback(() => {
    stopCamera();
    navigate('/scan/output');
  }, [navigate, stopCamera]);

  // Start camera immediately on mount
  useEffect(() => {
    if (!canScan) return;
    let mounted = true;
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stream.getTracks().forEach((t) => t.stop());
        startCamera();
      })
      .catch(() => {
        // Permission not granted yet, show ready state briefly then try
        startCamera();
      });
    return () => { mounted = false; };
  }, [startCamera, canScan]);

  if (!canScan) {
    return null;
  }

  return (
    <div className={`flex flex-col ${t.pageBg} relative overflow-hidden transition-colors duration-500 h-full`}>
      {/* Header */}
      <motion.div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 pt-6 pb-4"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
      >
        <Pressable
          onClick={() => { stopCamera(); navigate('/'); }}
          className={`w-10 h-10 rounded-full ${t.btnOverlay} backdrop-blur-md flex items-center justify-center`}
          scale={0.88}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-close-line text-white text-lg" />
          </div>
        </Pressable>
        <motion.div
          className={`${t.btnOverlay} backdrop-blur-md rounded-full px-4 py-2`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 400, damping: 24 }}
        >
          <span className="text-white text-xs font-semibold">Scan Homework</span>
        </motion.div>
        <div className="w-10" />
      </motion.div>

      {/* Camera / Preview Area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden min-h-0">
        <AnimatePresence mode="wait">
          {capturedImage ? (
            <motion.div
              key="preview"
              className="absolute inset-0 flex items-center justify-center bg-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            >
              <motion.img
                initial={{ scale: 1.08, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
                src={capturedImage}
                alt="Captured homework"
                className="w-full h-full object-contain"
              />
            </motion.div>
          ) : cameraError ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="text-center px-8"
            >
              <motion.div
                className={`w-16 h-16 rounded-full ${isDark ? 'bg-neutral-800' : 'bg-neutral-100'} flex items-center justify-center mx-auto mb-5`}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 350, damping: 20, delay: 0.1 }}
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`ri-camera-off-line ${t.textMuted} text-2xl`} />
                </div>
              </motion.div>
              <motion.h3
                className={`${t.textPrimary} text-base font-bold mb-2`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                Camera Access Needed
              </motion.h3>
              <motion.p
                className={`${t.textMuted} text-sm mb-6 leading-relaxed`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.3 }}
              >
                Allow camera access to scan your homework instantly.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.36, duration: 0.3 }}
              >
                <Pressable onClick={startCamera} className={`${t.btnPrimary} px-6 py-3 rounded-xl text-sm font-semibold mb-3 w-full`} scale={0.96}>
                  Open Camera
                </Pressable>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="active"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              <motion.video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.06, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
              />
              {/* Viewfinder Overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className={`absolute inset-0 ${t.overlayTint}`} />
                <motion.div
                  className={`absolute inset-12 rounded-2xl ${t.frameBorder}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
                >
                  <div className={`absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 ${t.frameCorner}`} />
                  <div className={`absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 ${t.frameCorner}`} />
                  <div className={`absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 ${t.frameCorner}`} />
                  <div className={`absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 ${t.frameCorner}`} />
                </motion.div>
              </div>
              <motion.div
                className="absolute top-24 left-0 right-0 text-center z-10"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              >
                <motion.p
                  className={`${t.hintText} text-xs font-medium ${t.hintBg} backdrop-blur-sm inline-block px-4 py-1.5 rounded-full`}
                  animate={{ opacity: [0.65, 1, 0.65] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  Align homework within the frame
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Capture Flash */}
      <AnimatePresence>
        {showFlash && (
          <motion.div
            className="absolute inset-0 z-50 bg-white pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      {/* Bottom Capture Controls */}
      <AnimatePresence>
        {cameraActive && !capturedImage && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="absolute bottom-0 left-0 right-0 z-20 pb-10 pt-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
          >
            <div className="flex items-center justify-center">
              <motion.button
                onClick={takePicture}
                disabled={isCapturing}
                className={`w-[76px] h-[76px] rounded-full border-[3px] ${t.captureRing} flex items-center justify-center cursor-pointer relative`}
                whileTap={{ scale: 0.86 }}
                transition={{ type: 'spring', stiffness: 500, damping: 18 }}
              >
                <motion.div className="absolute inset-0 rounded-full border border-white/20" animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0, 0.4] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.div className={`w-[56px] h-[56px] rounded-full ${t.captureFill}`} animate={isCapturing ? { scale: 0.55 } : { scale: 1 }} transition={{ type: 'spring', stiffness: 600, damping: 20 }} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Controls — minimal: Retake + Continue */}
      <AnimatePresence>
        {capturedImage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            className={`absolute bottom-0 left-0 right-0 z-20 ${t.bottomBar} backdrop-blur-xl px-6 py-6`}
          >
            <div className="flex items-center gap-3">
              <motion.div className="flex-1" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15, type: 'spring', stiffness: 400, damping: 26 }}>
                <Pressable
                  onClick={retake}
                  className={`w-full ${t.btnSecondary} py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2`}
                  scale={0.96}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-refresh-line text-sm" />
                  </div>
                  Retake
                </Pressable>
              </motion.div>
              <motion.div className="flex-1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.22, type: 'spring', stiffness: 400, damping: 26 }}>
                <Pressable
                  onClick={confirm}
                  className={`w-full ${t.btnPrimary} py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2`}
                  scale={0.96}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                  Continue
                </Pressable>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <DemoGate
        feature="scan"
        open={!!activeGate}
        onClose={dismissGate}
      />
    </div>
  );
}